import { PayloadAction } from '@reduxjs/toolkit';
import { call, fork, put, select,takeLatest } from 'redux-saga/effects';

import type { RootState } from '@/app/store';
import { userActions } from '@/entities/user';
import type { CurrencyCode } from '@/shared/lib/currency';
import { twa } from '@/shared/lib/telegram/twa';

import { minesApi, MinesCashoutResponse,MinesClickResponse } from '../api/minesApi';
import { minesActions } from './slice';
import { MinesActiveGame } from './types';

function* initMinesSaga(): Generator {
  try {
    const game = (yield call(minesApi.getActive)) as MinesActiveGame | null;
    if (game) {
      yield put(minesActions.setActiveGame(game));
    }
  } catch {
    // ignore, user just has no active game
  }
}

function* startGameSaga(
  action: PayloadAction<{ betAmount: number; minesCount: number; currency: CurrencyCode; giftId?: string }>,
): Generator {
  yield put(minesActions.setLoading(true));
  try {
    const clientSeed = crypto.randomUUID();
    const game = (yield call(
      minesApi.start,
      action.payload.betAmount,
      action.payload.minesCount,
      clientSeed,
      action.payload.currency,
      action.payload.giftId,
    )) as MinesActiveGame;

    yield put(minesActions.setActiveGame(game));
    yield put(
      userActions.updateBalanceByCurrency({
        currency: action.payload.currency,
        amount: -action.payload.betAmount,
      }),
    );
    twa.hapticFeedback.notificationOccurred('success');
  } catch {
    yield put(minesActions.setLoading(false));
    twa.hapticFeedback.notificationOccurred('error');
  }
}

function* clickCellSaga(action: PayloadAction<number>): Generator {
  yield put(minesActions.setPendingCell(action.payload));
  try {
    const response = (yield call(minesApi.click, action.payload)) as MinesClickResponse;

    if (response.isMine) {
      localStorage.removeItem('mines_radar_animated');
      yield put(minesActions.gameBusted({
        minePositions: response.minePositions!,
        serverSeed: response.serverSeed!,
        payout: 0,
        multiplier: 0,
      }));
      twa.hapticFeedback.notificationOccurred('error');
    } else {
      yield put(minesActions.cellRevealed({
        revealedPositions: response.revealedPositions,
        currentMultiplier: response.currentMultiplier!,
        nextMultiplier: response.nextMultiplier ?? null,
        radarHint: response.radarHint ?? null,
      }));
      twa.hapticFeedback.impactOccurred('light');
    }
  } catch {
    yield put(minesActions.setPendingCell(null));
    twa.hapticFeedback.notificationOccurred('error');
  }
}

function* cashoutSaga(): Generator {
  yield put(minesActions.setLoading(true));
  try {
    localStorage.removeItem('mines_radar_animated');
    const response = (yield call(minesApi.cashout)) as MinesCashoutResponse;

    yield put(minesActions.gameCashedOut({
      minePositions: response.minePositions,
      serverSeed: response.serverSeed,
      payout: response.payout,
      multiplier: response.multiplier,
    }));
    const currency = ((yield select((s: RootState) => s.mines.game?.currency ?? 'TON')) as string);
    yield put(userActions.updateBalanceByCurrency({ currency, amount: response.payout }));
    twa.hapticFeedback.notificationOccurred('success');
  } catch {
    yield put(minesActions.setLoading(false));
    twa.hapticFeedback.notificationOccurred('error');
  }
}

export function* minesWatcher(): Generator {
  yield fork(initMinesSaga);
  yield takeLatest(minesActions.startGame.type, startGameSaga);
  yield takeLatest(minesActions.clickCell.type, clickCellSaga);
  yield takeLatest(minesActions.cashout.type, cashoutSaga);
}
